import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Calendar } from './ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from './ui/popover';
import { toast } from 'sonner';
import { Calendar as CalendarIcon, Clock, MapPin, Euro, User, Phone, Mail } from 'lucide-react';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';
import { services, fleet } from '../mockData';
import axios from 'axios';
import { useLanguage } from '../LanguageContext';

// Refactored components
import BookingFormFields from './booking/BookingFormFields';
import DateTimePicker from './booking/DateTimePicker';
import LocationFields from './booking/LocationFields';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const BookingSection = () => {
  const { t } = useLanguage();
  const pickupInputRef = useRef(null);
  const dropoffInputRef = useRef(null);
  const pickupAutocompleteRef = useRef(null);
  const dropoffAutocompleteRef = useRef(null);
  
  // Customer auth state
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [customerData, setCustomerData] = useState(null);
  const [loyaltyDiscount, setLoyaltyDiscount] = useState(0);
  
  const [bookingData, setBookingData] = useState({
    serviceType: 'transfer', // transfer, corporate, hourly
    vehicle: '',
    date: null,
    time: '',
    pickup: '',
    dropoff: '',
    distance: 0,
    duration: 0,
    hours: 3, // For hourly booking
    customerName: '',
    customerEmail: '',
    customerPhone: '',
    isRoundTrip: false,
    returnDate: null,
    returnTime: '',
    isOutsideBrussels: false, // Détecté automatiquement
    zoneType: 'standard', // standard, enlarged, outOfZone
    paymentMethod: 'online', // NEW: online or cash
    // Prix et réductions
    calculatedPrice: 0, // Prix calculé automatiquement
    discountType: 'none', // 'none', 'percentage', 'fixed', 'manual'
    discountValue: 0, // Valeur de la réduction (% ou €)
    manualPrice: 0, // Prix manuel si discountType = 'manual'
    finalPrice: 0 // Prix final après réduction
  });
  
  const [estimatedPrice, setEstimatedPrice] = useState(0);
  const [showPriceOverride, setShowPriceOverride] = useState(false);
  const [loading, setLoading] = useState(false);
  const [calculatingDistance, setCalculatingDistance] = useState(false);
  const [errors, setErrors] = useState({}); // Track validation errors
  const [pricingRules, setPricingRules] = useState({
    basePrice: 8.0,
    pricePerKm: 1.50,
    pricePerMinute: 0.40,
    corporatePerHour: 60.0,
    hourlyRate: 75.0,
    hourlyMinimum: 3,
    outsideBrusselsSurcharge: 30.0 // Supplément "Zone élargie" pour trajets hors Bruxelles
  });

  // Calculate minimum time (30 minutes from now)
  const getMinimumTime = () => {
    const now = new Date();
    now.setMinutes(now.getMinutes() + 30);
    return now.toTimeString().slice(0, 5); // Format HH:MM
  };


  // Load pricing rules from backend on mount
  useEffect(() => {
    const fetchPricing = async () => {
      try {
        // Note: Using default pricing rules for now
        // If backend has a public pricing endpoint, use: axios.get(`${API}/pricing`)
        console.log('Using default pricing rules');
      } catch (error) {
        console.error('Error loading pricing:', error);
        // Use default values if error
      }
    };
    fetchPricing();
  }, []);

  // Check if customer is logged in and pre-fill data
  useEffect(() => {
    const token = localStorage.getItem('customerToken');
    const storedCustomerData = localStorage.getItem('customerData');
    
    if (token && storedCustomerData) {
      try {
        const data = JSON.parse(storedCustomerData);
        setIsLoggedIn(true);
        setCustomerData(data);
        
        // Pre-fill customer info
        setBookingData(prev => ({
          ...prev,
          customerName: data.name || '',
          customerEmail: data.email || '',
          customerPhone: data.phone || ''
        }));
        
        // Check loyalty eligibility (5 completed rides = 10% discount on next ride)
        const loyaltyPoints = data.loyaltyPoints || 0;
        if (loyaltyPoints > 0 && loyaltyPoints % 5 === 0) {
          setLoyaltyDiscount(10); // 10% discount
          toast.success('🎉 Réduction fidélité de 10% appliquée !', {
            duration: 5000
          });
        }
      } catch (e) {
        console.error('Error parsing customer data:', e);
      }
    }
  }, []);

  // Initialize Google Places Autocomplete
  useEffect(() => {
    if (!window.google || !pickupInputRef.current || !dropoffInputRef.current) return;

    try {
      // Pickup autocomplete
      pickupAutocompleteRef.current = new window.google.maps.places.Autocomplete(
        pickupInputRef.current,
        {
          types: ['geocode', 'establishment'],
          componentRestrictions: { country: ['be', 'fr', 'nl', 'de', 'lu'] }
        }
      );

      pickupAutocompleteRef.current.addListener('place_changed', () => {
        const place = pickupAutocompleteRef.current.getPlace();
        if (place) {
          // Use name for airports/stations, otherwise use formatted_address
          let address = place.formatted_address || place.name;
          
          // If it's a point of interest (airport, station, etc.), use name
          if (place.name && place.types && (
            place.types.includes('airport') || 
            place.types.includes('train_station') ||
            place.types.includes('bus_station') ||
            place.types.includes('transit_station')
          )) {
            address = place.name;
          }
          
          setBookingData(prev => ({ ...prev, pickup: address }));
        }
      });

      // Dropoff autocomplete
      dropoffAutocompleteRef.current = new window.google.maps.places.Autocomplete(
        dropoffInputRef.current,
        {
          types: ['geocode', 'establishment'],
          componentRestrictions: { country: ['be', 'fr', 'nl', 'de', 'lu'] }
        }
      );

      dropoffAutocompleteRef.current.addListener('place_changed', () => {
        const place = dropoffAutocompleteRef.current.getPlace();
        if (place) {
          // Use name for airports/stations, otherwise use formatted_address
          let address = place.formatted_address || place.name;
          
          // If it's a point of interest (airport, station, etc.), use name
          if (place.name && place.types && (
            place.types.includes('airport') || 
            place.types.includes('train_station') ||
            place.types.includes('bus_station') ||
            place.types.includes('transit_station')
          )) {
            address = place.name;
          }
          
          setBookingData(prev => ({ ...prev, dropoff: address }));
        }
      });
    } catch (error) {
      console.error('Error initializing Google Places:', error);
    }
  }, []);

  // Auto-calculate price when service type or hours change (for hourly/corporate)
  useEffect(() => {
    if (bookingData.serviceType === 'hourly' || bookingData.serviceType === 'corporate') {
      const price = calculatePrice(bookingData.serviceType, 0, 0, bookingData.hours);
      setEstimatedPrice(price);
    }
  }, [bookingData.serviceType, bookingData.hours, pricingRules]);

  // Recalculate price when isRoundTrip or loyalty changes
  useEffect(() => {
    if (bookingData.serviceType === 'transfer' && bookingData.distance && bookingData.duration) {
      const price = calculatePrice(bookingData.serviceType, bookingData.distance, bookingData.duration, bookingData.hours, bookingData.isOutsideBrussels);
      setEstimatedPrice(price);
    }
  }, [bookingData.isRoundTrip, loyaltyDiscount, bookingData.isOutsideBrussels]);

  // Auto-calculate distance when BOTH pickup AND dropoff are filled
  useEffect(() => {
    // Only for transfer service
    if (bookingData.serviceType !== 'transfer') return;
    
    // Check if both addresses are filled (at least 10 characters each)
    if (!bookingData.pickup || bookingData.pickup.length < 10) return;
    if (!bookingData.dropoff || bookingData.dropoff.length < 10) return;
    
    // Debounce: wait 2 seconds after user stops typing
    const timer = setTimeout(() => {
      if (!calculatingDistance) {
        console.log('🚀 Auto-calculating distance...');
        handleCalculateDistance();
      }
    }, 2000);
    
    return () => clearTimeout(timer);
  }, [bookingData.pickup, bookingData.dropoff, bookingData.serviceType]);

  // Force 24-hour format for time inputs on component mount
  useEffect(() => {
    const timeInputs = document.querySelectorAll('input[type="time"]');
    timeInputs.forEach(input => {
      // Force 24h format
      input.style.colorScheme = 'dark';
    });
  }, []);

  const calculatePrice = (serviceType, distance, duration, hours, isOutsideBrussels = false) => {
    let basePrice = 0;
    
    if (serviceType === 'transfer') {
      if (!distance || !duration) return 0;
      basePrice = pricingRules.basePrice + (distance * pricingRules.pricePerKm) + (duration * pricingRules.pricePerMinute);
      
      // Ajouter supplément si départ Bruxelles → Destination hors Bruxelles
      if (isOutsideBrussels) {
        basePrice += pricingRules.outsideBrusselsSurcharge;
      }
    } else if (serviceType === 'corporate') {
      if (!hours) return 0;
      basePrice = pricingRules.basePrice + (hours * pricingRules.corporatePerHour);
    } else if (serviceType === 'hourly') {
      const minHours = Math.max(hours || 0, pricingRules.hourlyMinimum);
      basePrice = minHours * pricingRules.hourlyRate;
    }
    
    // Apply round-trip discount (10% off for transfer only)
    if (serviceType === 'transfer' && bookingData.isRoundTrip) {
      basePrice = basePrice * 0.9; // -10%
    }
    
    // Apply loyalty discount
    if (loyaltyDiscount > 0) {
      basePrice = basePrice * (1 - loyaltyDiscount / 100);
    }
    
    return Math.round(basePrice * 100) / 100; // Round to 2 decimals
  };

  // Calculer le prix final avec réduction/modification manuelle
  const calculateFinalPrice = () => {
    const basePrice = estimatedPrice;
    let finalPrice = basePrice;
    
    if (bookingData.discountType === 'percentage' && bookingData.discountValue > 0) {
      // Réduction en pourcentage (max 50%)
      const discount = Math.min(bookingData.discountValue, 50);
      finalPrice = basePrice * (1 - discount / 100);
    } else if (bookingData.discountType === 'fixed' && bookingData.discountValue > 0) {
      // Réduction en montant fixe (max 50% du prix)
      const maxDiscount = basePrice * 0.5;
      const discount = Math.min(bookingData.discountValue, maxDiscount);
      finalPrice = basePrice - discount;
    } else if (bookingData.discountType === 'manual' && bookingData.manualPrice > 0) {
      // Prix manuel (minimum 50% du prix calculé)
      const minPrice = basePrice * 0.5;
      finalPrice = Math.max(bookingData.manualPrice, minPrice);
    } else {
      // Aucune modification
      finalPrice = basePrice;
    }
    
    // Prix minimum = 50% du prix de base
    const minPrice = basePrice * 0.5;
    finalPrice = Math.max(minPrice, finalPrice);
    
    return Math.round(finalPrice * 100) / 100;
  };

  // Mettre à jour le prix final quand les réductions changent
  useEffect(() => {
    if (estimatedPrice > 0) {
      const final = calculateFinalPrice();
      setBookingData(prev => ({
        ...prev,
        calculatedPrice: estimatedPrice,
        finalPrice: final
      }));
    }
  }, [estimatedPrice, bookingData.discountType, bookingData.discountValue, bookingData.manualPrice]);

  const handleInputChange = (field, value) => {
    setBookingData(prev => ({ ...prev, [field]: value }));
    
    // Recalculate price if distance, duration, or hours changed manually
    if (field === 'distance' || field === 'duration' || field === 'hours') {
      const newData = { ...bookingData, [field]: value };
      const newPrice = calculatePrice(newData.serviceType, newData.distance, newData.duration, newData.hours || value);
      setEstimatedPrice(newPrice);
    }
    
    // Recalculate when service type changes
    if (field === 'serviceType') {
      // Reset relevant fields when changing service type
      const newData = { ...bookingData, serviceType: value };
      const newPrice = calculatePrice(value, newData.distance, newData.duration, newData.hours);
      setEstimatedPrice(newPrice);
    }
  };

  const handleCalculateDistance = async () => {
    if (!bookingData.pickup || !bookingData.dropoff) {
      toast.error('Veuillez entrer les adresses de départ et d\'arrivée');
      return;
    }
    
    setCalculatingDistance(true);
    
    try {
      // Combine date and time for departure
      let departureDateTime = null;
      if (bookingData.date && bookingData.time) {
        const dateStr = format(bookingData.date, 'yyyy-MM-dd');
        departureDateTime = `${dateStr}T${bookingData.time}:00`;
      }
      
      console.log('📍 Calcul pour:', { 
        pickup: bookingData.pickup, 
        dropoff: bookingData.dropoff 
      });
      
      const response = await axios.post(`${API}/calculate-distance`, {
        origin: bookingData.pickup,
        destination: bookingData.dropoff,
        departure_datetime: departureDateTime
      });
      
      console.log('📊 Réponse API:', response.data);
      
      // Round distance to nearest kilometer (no decimals)
      const distanceRounded = Math.round(response.data.distance_km);
      
      // Update distance and duration (no buffer - use real Google Maps duration)
      const durationReal = response.data.duration_in_traffic_minutes;
      
      // DÉTECTION AUTOMATIQUE PAR DISTANCE ET LOCALISATION
      // Standard Bruxelles: < 30 km OU au moins une adresse à Bruxelles
      // Zone élargie: 30-100 km ET les DEUX adresses hors Bruxelles (périphérie-périphérie)
      // Hors zone: > 100 km (Flandre, Wallonie)
      // EXCEPTION: Trajets vers/depuis Zaventem = toujours zone Bruxelles (pas de supplément)
      
      let zoneType = 'standard';
      let isOutsideBrussels = false;
      
      // Vérifier si le trajet concerne l'aéroport de Zaventem
      const originAddress = response.data.origin_address.toLowerCase();
      const destinationAddress = response.data.destination_address.toLowerCase();
      const isZaventemTrip = originAddress.includes('zaventem') || 
                            originAddress.includes('airport') || 
                            originAddress.includes('aéroport') ||
                            destinationAddress.includes('zaventem') || 
                            destinationAddress.includes('airport') || 
                            destinationAddress.includes('aéroport');
      
      // Détecter si une adresse est à Bruxelles (ville ou communes)
      const brusselsKeywords = [
        'bruxelles', 'brussels', 'brussel',
        'ixelles', 'uccle', 'schaerbeek', 'anderlecht', 'molenbeek',
        'etterbeek', 'saint-gilles', 'forest', 'woluwe', 'koekelberg',
        'jette', 'ganshoren', 'berchem', 'evere', 'auderghem',
        'watermael', 'boitsfort'
      ];
      
      const isOriginInBrussels = brusselsKeywords.some(keyword => originAddress.includes(keyword));
      const isDestinationInBrussels = brusselsKeywords.some(keyword => destinationAddress.includes(keyword));
      
      // Zone élargie UNIQUEMENT si les DEUX adresses sont hors Bruxelles
      const bothOutsideBrussels = !isOriginInBrussels && !isDestinationInBrussels;
      
      if (distanceRounded >= 100) {
        zoneType = 'outOfZone';
        isOutsideBrussels = true; // Supplément de 50€
      } else if (distanceRounded >= 30 && !isZaventemTrip && bothOutsideBrussels) {
        // Zone élargie UNIQUEMENT si :
        // - Distance 30-100 km
        // - PAS un trajet Zaventem
        // - Les DEUX adresses sont hors Bruxelles
        zoneType = 'enlarged';
        isOutsideBrussels = true; // Supplément de 30€
      } else {
        // Tous les autres cas = zone Bruxelles standard
        // - < 30 km
        // - OU trajet Zaventem
        // - OU au moins une adresse à Bruxelles
        zoneType = 'standard';
        isOutsideBrussels = false; // Pas de supplément
      }
      
      console.log('🔍 Détection PAR DISTANCE ET LOCALISATION:', { 
        distance: distanceRounded,
        zoneType,
        isOutsideBrussels,
        isZaventemTrip,
        isOriginInBrussels,
        isDestinationInBrussels,
        bothOutsideBrussels,
        origin: response.data.origin_address,
        destination: response.data.destination_address
      });
      
      console.log(`🎯 RÉSULTAT: ${isOutsideBrussels ? `⚠️ ${zoneType === 'outOfZone' ? 'HORS ZONE (+50€)' : 'ZONE ÉLARGIE (+30€) - Périphérie vers Périphérie'}` : isZaventemTrip && distanceRounded >= 30 ? '✅ ZONE BRUXELLES (Zaventem - pas de supplément)' : !bothOutsideBrussels && distanceRounded >= 30 ? '✅ ZONE BRUXELLES (au moins une adresse à Bruxelles - pas de supplément)' : '✅ ZONE BRUXELLES (8€ base)'}`);
      
      setBookingData(prev => ({
        ...prev,
        distance: distanceRounded,
        duration: durationReal,
        isOutsideBrussels: isOutsideBrussels,
        zoneType: zoneType
      }));
      
      // Calculate price avec supplément si applicable
      const price = calculatePrice(bookingData.serviceType, distanceRounded, durationReal, bookingData.hours, isOutsideBrussels);
      setEstimatedPrice(price);
      
      if (distanceRounded >= 100) {
        toast.success(`✅ ${distanceRounded} km, ${durationReal} min | ⚠️ Hors zone: +50€`, { duration: 5000 });
      } else if (distanceRounded >= 30) {
        toast.success(`✅ ${distanceRounded} km, ${durationReal} min | ⚠️ Zone élargie: +${pricingRules.outsideBrusselsSurcharge}€`, { duration: 5000 });
      } else {
        toast.success(`✅ ${distanceRounded} km, ${durationReal} min | Zone Bruxelles (8€ base)`, { duration: 4000 });
      }
      
    } catch (error) {
      console.error('❌ Erreur calcul distance:', error);
      console.error('Détails:', error.response?.data);
      toast.error(`Erreur: ${error.response?.data?.detail || error.message || 'Vérifiez les adresses'}`);
    } finally {
      setCalculatingDistance(false);
    }
  };

  const handleSubmit = async () => {
    // Validate all required fields and track errors
    const newErrors = {};
    
    // Basic required fields
    if (!bookingData.vehicle) newErrors.vehicle = 'Veuillez sélectionner un véhicule';
    if (!bookingData.date) newErrors.date = 'Veuillez sélectionner une date';
    if (!bookingData.time) newErrors.time = 'Veuillez sélectionner une heure';
    if (!bookingData.customerName || bookingData.customerName.trim() === '') {
      newErrors.customerName = 'Veuillez entrer votre nom complet';
    }
    if (!bookingData.customerEmail || bookingData.customerEmail.trim() === '') {
      newErrors.customerEmail = 'Veuillez entrer votre email';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(bookingData.customerEmail)) {
      newErrors.customerEmail = 'Email invalide';
    }
    if (!bookingData.customerPhone || bookingData.customerPhone.trim() === '') {
      newErrors.customerPhone = 'Veuillez entrer votre téléphone';
    }

    // Service-specific validation
    if (bookingData.serviceType === 'transfer') {
      if (!bookingData.pickup || bookingData.pickup.trim() === '') {
        newErrors.pickup = 'Veuillez entrer l\'adresse de prise en charge';
      }
      if (!bookingData.dropoff || bookingData.dropoff.trim() === '') {
        newErrors.dropoff = 'Veuillez entrer l\'adresse de destination';
      }
      // Check if distance was calculated
      if (bookingData.pickup && bookingData.dropoff && (!bookingData.distance || !bookingData.duration)) {
        newErrors.distance = 'Veuillez calculer la distance en cliquant sur "Calculer le Prix"';
      }
      
      // Round trip validation
      if (bookingData.isRoundTrip) {
        if (!bookingData.returnDate) newErrors.returnDate = 'Veuillez sélectionner la date de retour';
        if (!bookingData.returnTime || bookingData.returnTime.trim() === '') {
          newErrors.returnTime = 'Veuillez sélectionner l\'heure de retour';
        }
      }
    } else if (bookingData.serviceType === 'corporate' || bookingData.serviceType === 'hourly') {
      if (!bookingData.pickup || bookingData.pickup.trim() === '') {
        newErrors.pickup = 'Veuillez entrer l\'adresse de prise en charge';
      }
      if (!bookingData.hours || bookingData.hours < 1) {
        newErrors.hours = 'Veuillez entrer le nombre d\'heures';
      }
      if (bookingData.serviceType === 'hourly' && bookingData.hours < 3) {
        newErrors.hours = 'Minimum 3 heures pour une réservation horaire';
      }
    }
    
    // Check if price is calculated
    if (!estimatedPrice || estimatedPrice <= 0) {
      newErrors.price = 'Le prix n\'a pas pu être calculé. Vérifiez les informations saisies.';
    }
    
    // If there are errors, update state and show error message
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      toast.error('❌ Veuillez remplir tous les champs obligatoires en rouge');
      // Scroll to first error
      const firstErrorField = Object.keys(newErrors)[0];
      const errorElement = document.querySelector(`[name="${firstErrorField}"]`);
      if (errorElement) {
        errorElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
      return;
    }
    
    // Clear errors if validation passes
    setErrors({});
    
    setLoading(true);
    
    try {
      // Prepare booking data
      const bookingPayload = {
        service: bookingData.serviceType,
        vehicle: bookingData.vehicle,
        date: format(bookingData.date, 'yyyy-MM-dd'),
        time: bookingData.time,
        pickup: bookingData.pickup,
        dropoff: bookingData.dropoff || '',
        hours: bookingData.hours || 0,
        distance: bookingData.distance || 0,
        estimatedPrice: estimatedPrice,
        customerName: bookingData.customerName,
        customerEmail: bookingData.customerEmail,
        customerPhone: bookingData.customerPhone,
        isRoundTrip: bookingData.isRoundTrip || false,
        returnDate: bookingData.returnDate ? format(bookingData.returnDate, 'yyyy-MM-dd') : null,
        returnTime: bookingData.returnTime || null,
        paymentMethod: bookingData.paymentMethod || 'online'  // AJOUT: Envoi du mode de paiement
      };
      
      // Send to backend v2
      const response = await axios.post(`${API}/bookings/v2`, bookingPayload);
      
      toast.success(t('booking.success.title', { reference: response.data.id }), {
        description: t('booking.success.emailSent', { email: bookingData.customerEmail }),
        duration: 5000
      });
      
      // Reset form
      setBookingData({
        serviceType: 'transfer',
        vehicle: '',
        date: null,
        time: '',
        pickup: '',
        dropoff: '',
        distance: 0,
        duration: 0,
        hours: 3,
        customerName: '',
        customerEmail: '',
        customerPhone: '',
        isRoundTrip: false,
        returnDate: null,
        returnTime: ''
      });
      setEstimatedPrice(0);
      
    } catch (error) {
      console.error('Booking error:', error);
      toast.error('Erreur lors de la réservation. Veuillez réessayer.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="booking-section" className="py-24 bg-gradient-to-b from-black to-gray-900">
      <div className="container mx-auto px-6">
        <div className="text-center mb-12">
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-4">{t('booking.title')}</h2>
          <p className="text-xl text-gray-400">{t('booking.subtitle')}</p>
        </div>
        
        <Card className="max-w-4xl mx-auto bg-gradient-to-br from-gray-900 to-black border-2 border-gold/30 shadow-2xl shadow-gold/10">
          <CardHeader>
            <CardTitle className="text-3xl text-white">{t('booking.detailsTitle')}</CardTitle>
            <CardDescription className="text-gray-400 text-lg">
              {t('booking.detailsSubtitle')}
            </CardDescription>
          </CardHeader>
          
          <CardContent>
            <form className="space-y-6">
              {/* Customer Information Section */}
              <div className="bg-gradient-to-r from-gold/10 to-amber-600/10 border border-gold/30 rounded-lg p-6 mb-6">
                <h3 className="text-xl font-semibold text-white mb-4 flex items-center gap-2">
                  <User className="w-5 h-5 text-gold" />
                  {t('booking.yourInfo')}
                </h3>
                
                {/* Login prompt for non-logged users */}
                {!isLoggedIn && (
                  <div className="bg-purple-900/30 border border-purple-500/50 rounded-lg p-4 mb-4">
                    <p className="text-purple-300 text-sm mb-2">
                      💡 <strong>Créez un compte</strong> pour bénéficier du programme fidélité !
                    </p>
                    <p className="text-purple-400 text-xs mb-3">
                      5 courses = 10% de réduction sur la 6ème
                    </p>
                    <div className="flex gap-2">
                      <a 
                        href="/client/signup" 
                        className="text-xs px-3 py-1.5 bg-purple-600 hover:bg-purple-700 text-white rounded-md transition-colors"
                      >
                        Inscription
                      </a>
                      <a 
                        href="/client/login" 
                        className="text-xs px-3 py-1.5 border border-purple-500 text-purple-300 hover:bg-purple-500/20 rounded-md transition-colors"
                      >
                        Connexion
                      </a>
                    </div>
                  </div>
                )}
                
                <div className="grid md:grid-cols-3 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="customerName" className="text-white text-base">{t('booking.fullName')} *</Label>
                    <div className="relative">
                      <User className="absolute left-3 top-3 h-4 w-4 text-gold" />
                      <Input
                        id="customerName"
                        name="customerName"
                        placeholder={t('booking.fullNamePlaceholder')}
                        value={bookingData.customerName}
                        onChange={(e) => {
                          handleInputChange('customerName', e.target.value);
                          if (errors.customerName) setErrors({...errors, customerName: null});
                        }}
                        className={`bg-gray-800 text-white pl-10 ${errors.customerName ? 'border-red-500 border-2' : 'border-gold/30'}`}
                        required
                      />
                    </div>
                    {errors.customerName && (
                      <p className="text-red-500 text-sm flex items-center gap-1">
                        <span>⚠️</span> {errors.customerName}
                      </p>
                    )}
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="customerEmail" className="text-white text-base">{t('booking.email')} *</Label>
                    <div className="relative">
                      <Mail className="absolute left-3 top-3 h-4 w-4 text-gold" />
                      <Input
                        id="customerEmail"
                        name="customerEmail"
                        type="email"
                        placeholder={t('booking.emailPlaceholder')}
                        value={bookingData.customerEmail}
                        onChange={(e) => {
                          handleInputChange('customerEmail', e.target.value);
                          if (errors.customerEmail) setErrors({...errors, customerEmail: null});
                        }}
                        className={`bg-gray-800 text-white pl-10 ${errors.customerEmail ? 'border-red-500 border-2' : 'border-gold/30'}`}
                        required
                      />
                    </div>
                    {errors.customerEmail && (
                      <p className="text-red-500 text-sm flex items-center gap-1">
                        <span>⚠️</span> {errors.customerEmail}
                      </p>
                    )}
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="customerPhone" className="text-white text-base">{t('booking.phone')} *</Label>
                    <div className="relative">
                      <Phone className="absolute left-3 top-3 h-4 w-4 text-gold" />
                      <Input
                        id="customerPhone"
                        name="customerPhone"
                        type="tel"
                        placeholder={t('booking.phonePlaceholder')}
                        value={bookingData.customerPhone}
                        onChange={(e) => {
                          handleInputChange('customerPhone', e.target.value);
                          if (errors.customerPhone) setErrors({...errors, customerPhone: null});
                        }}
                        className={`bg-gray-800 text-white pl-10 ${errors.customerPhone ? 'border-red-500 border-2' : 'border-gold/30'}`}
                        required
                      />
                    </div>
                    {errors.customerPhone && (
                      <p className="text-red-500 text-sm flex items-center gap-1">
                        <span>⚠️</span> {errors.customerPhone}
                      </p>
                    )}
                  </div>
                </div>
              </div>

              <div className="grid md:grid-cols-2 gap-6">
                {/* Service Type Selection */}
                <div className="space-y-2 md:col-span-2">
                  <Label htmlFor="serviceType" className="text-white text-base">{t('booking.serviceType')} *</Label>
                  <Select value={bookingData.serviceType} onValueChange={(value) => handleInputChange('serviceType', value)}>
                    <SelectTrigger className="bg-gray-800 border-gold/30 text-white">
                      <SelectValue placeholder={t('booking.serviceTypePlaceholder')} />
                    </SelectTrigger>
                    <SelectContent>
                      {services.map(service => (
                        <SelectItem key={service.id} value={service.value}>
                          {service.title}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-gray-500">
                    {bookingData.serviceType === 'transfer' && t('booking.serviceDescriptions.transfer')}
                    {bookingData.serviceType === 'corporate' && t('booking.serviceDescriptions.corporate')}
                    {bookingData.serviceType === 'hourly' && t('booking.serviceDescriptions.hourly')}
                  </p>
                </div>
                {/* Vehicle Selection */}
                <div className="space-y-2">
                  <Label htmlFor="vehicle" className="text-white text-base">{t('booking.vehicle')} *</Label>
                  <Select 
                    value={bookingData.vehicle} 
                    onValueChange={(value) => {
                      handleInputChange('vehicle', value);
                      if (errors.vehicle) setErrors({...errors, vehicle: null});
                    }}
                  >
                    <SelectTrigger className={`bg-gray-800 text-white ${errors.vehicle ? 'border-red-500 border-2' : 'border-gold/30'}`} name="vehicle">
                      <SelectValue placeholder={t('booking.vehiclePlaceholder')} />
                    </SelectTrigger>
                    <SelectContent>
                      {fleet.map(vehicle => (
                        <SelectItem key={vehicle.id} value={vehicle.name}>
                          <div className="flex items-center gap-2">
                            {vehicle.name}
                            {vehicle.badge === 'Recommandé' && (
                              <span className="text-xs bg-green-500/20 text-green-400 px-2 py-0.5 rounded">✓ Recommandé</span>
                            )}
                            {vehicle.badge === 'Premium' && (
                              <span className="text-xs bg-gold/20 text-gold px-2 py-0.5 rounded">★ Premium</span>
                            )}
                          </div>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {errors.vehicle && (
                    <p className="text-red-500 text-sm flex items-center gap-1">
                      <span>⚠️</span> {errors.vehicle}
                    </p>
                  )}
                </div>

                {/* Date */}
                <div className="space-y-2">
                  <Label className="text-white text-base">{t('booking.date')} *</Label>
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button
                        type="button"
                        variant="outline"
                        name="date"
                        className={`w-full justify-start bg-gray-800 text-white hover:bg-gray-700 ${errors.date ? 'border-red-500 border-2' : 'border-gold/30'}`}
                      >
                        <CalendarIcon className="mr-2 h-4 w-4 text-gold" />
                        {bookingData.date ? format(bookingData.date, 'dd/MM/yyyy', { locale: fr }) : t('booking.datePlaceholder')}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0 bg-gray-800 border-gold/30">
                      <Calendar
                        mode="single"
                        selected={bookingData.date}
                        onSelect={(date) => {
                          handleInputChange('date', date);
                          if (errors.date) setErrors({...errors, date: null});
                        }}
                        disabled={(date) => {
                          const today = new Date();
                          today.setHours(0, 0, 0, 0);
                          return date < today;
                        }}
                        locale={fr}
                        initialFocus
                      />
                    </PopoverContent>
                  </Popover>
                  {errors.date && (
                    <p className="text-red-500 text-sm flex items-center gap-1">
                      <span>⚠️</span> {errors.date}
                    </p>
                  )}
                </div>

                {/* Time - Separate Hour and Minute Dropdowns */}
                <div className="space-y-2">
                  <Label className="text-white text-base">{t('booking.time')} *</Label>
                  <div className="flex gap-3 items-center">
                    {/* Hours Dropdown */}
                    <div className="flex-1">
                      <Select
                        value={bookingData.time.split(':')[0] || ''}
                        onValueChange={(hour) => {
                          const currentMinute = bookingData.time.split(':')[1] || '00';
                          handleInputChange('time', `${hour}:${currentMinute}`);
                          if (errors.time) setErrors({...errors, time: null});
                        }}
                      >
                        <SelectTrigger className={`w-full bg-gray-800 text-white hover:bg-gray-700 ${errors.time ? 'border-red-500 border-2' : 'border-gold/30'}`} name="time">
                          <div className="flex items-center justify-center">
                            <Clock className="mr-2 h-4 w-4 text-gold" />
                            {bookingData.time.split(':')[0] || 'HH'}
                          </div>
                        </SelectTrigger>
                        <SelectContent className="bg-gray-800 border-gold/30 max-h-[300px] overflow-y-auto">
                          {(() => {
                            const now = new Date();
                            const selectedDate = bookingData.date ? new Date(bookingData.date) : null;
                            const isToday = selectedDate && 
                              selectedDate.getDate() === now.getDate() &&
                              selectedDate.getMonth() === now.getMonth() &&
                              selectedDate.getFullYear() === now.getFullYear();
                            
                            let startHour = 0;
                            if (isToday) {
                              const minimumTime = new Date(now.getTime() + 30 * 60000);
                              startHour = minimumTime.getHours();
                            }
                            
                            const hours = [];
                            for (let h = startHour; h < 24; h++) {
                              hours.push(h.toString().padStart(2, '0'));
                            }
                            
                            return hours.map(hour => (
                              <SelectItem key={hour} value={hour} className="text-white hover:bg-gray-700">
                                {hour}
                              </SelectItem>
                            ));
                          })()}
                        </SelectContent>
                      </Select>
                      <p className="text-xs text-gray-400 mt-1 text-center">Heures</p>
                    </div>

                    <span className="text-gold text-2xl font-bold">:</span>

                    {/* Minutes Dropdown */}
                    <div className="flex-1">
                      <Select
                        value={bookingData.time.split(':')[1] || ''}
                        onValueChange={(minute) => {
                          const currentHour = bookingData.time.split(':')[0] || '00';
                          handleInputChange('time', `${currentHour}:${minute}`);
                          if (errors.time) setErrors({...errors, time: null});
                        }}
                      >
                        <SelectTrigger className={`w-full bg-gray-800 text-white hover:bg-gray-700 ${errors.time ? 'border-red-500 border-2' : 'border-gold/30'}`}>
                          <div className="flex items-center justify-center">
                            {bookingData.time.split(':')[1] || 'MM'}
                          </div>
                        </SelectTrigger>
                        <SelectContent className="bg-gray-800 border-gold/30 max-h-[300px] overflow-y-auto">
                          {(() => {
                            const now = new Date();
                            const selectedDate = bookingData.date ? new Date(bookingData.date) : null;
                            const isToday = selectedDate && 
                              selectedDate.getDate() === now.getDate() &&
                              selectedDate.getMonth() === now.getMonth() &&
                              selectedDate.getFullYear() === now.getFullYear();
                            
                            const selectedHour = bookingData.time.split(':')[0];
                            let startMinute = 0;
                            
                            if (isToday && selectedHour) {
                              const minimumTime = new Date(now.getTime() + 30 * 60000);
                              const minHour = minimumTime.getHours();
                              const minMinute = minimumTime.getMinutes();
                              
                              // Si l'heure sélectionnée est l'heure minimum, commencer aux minutes minimum
                              if (parseInt(selectedHour) === minHour) {
                                startMinute = minMinute;
                              }
                            }
                            
                            const minutes = [];
                            for (let m = startMinute; m < 60; m++) {
                              minutes.push(m.toString().padStart(2, '0'));
                            }
                            
                            return minutes.map(minute => (
                              <SelectItem key={minute} value={minute} className="text-white hover:bg-gray-700">
                                {minute}
                              </SelectItem>
                            ));
                          })()}
                        </SelectContent>
                      </Select>
                      <p className="text-xs text-gray-400 mt-1 text-center">Minutes</p>
                    </div>
                  </div>
                  {errors.time && (
                    <p className="text-red-500 text-sm flex items-center gap-1">
                      <span>⚠️</span> {errors.time}
                    </p>
                  )}
                  <p className="text-xs text-gray-500">Format 24h - Réservation minimum 30 minutes à l&apos;avance pour aujourd&apos;hui</p>
                </div>

                {/* Round Trip Checkbox - Only for transfer */}
                {bookingData.serviceType === 'transfer' && (
                  <div className="md:col-span-2 space-y-4">
                    <div className="flex items-center space-x-3 bg-gold/10 p-4 rounded-lg border border-gold/30">
                      <input
                        type="checkbox"
                        id="isRoundTrip"
                        checked={bookingData.isRoundTrip}
                        onChange={(e) => {
                          handleInputChange('isRoundTrip', e.target.checked);
                          if (!e.target.checked) {
                            handleInputChange('returnDate', null);
                            handleInputChange('returnTime', '');
                          }
                        }}
                        className="w-5 h-5 rounded border-gold text-gold focus:ring-gold"
                      />
                      <label htmlFor="isRoundTrip" className="text-white text-lg font-semibold cursor-pointer flex items-center gap-2">
                        🔄 Aller-Retour
                        <span className="text-sm font-normal text-gold">(-10% de réduction !)</span>
                      </label>
                    </div>

                    {bookingData.isRoundTrip && (
                      <div className="grid md:grid-cols-2 gap-4 pl-4 border-l-4 border-gold/50">
                        {/* Return Date */}
                        <div className="space-y-2">
                          <Label className="text-white text-base">Date de retour *</Label>
                          <Popover>
                            <PopoverTrigger asChild>
                              <Button
                                type="button"
                                variant="outline"
                                className="w-full justify-start bg-gray-800 border-gold/30 text-white hover:bg-gray-700"
                              >
                                <CalendarIcon className="mr-2 h-4 w-4 text-gold" />
                                {bookingData.returnDate ? format(bookingData.returnDate, 'dd/MM/yyyy', { locale: fr }) : 'Sélectionner'}
                              </Button>
                            </PopoverTrigger>
                            <PopoverContent className="w-auto p-0 bg-gray-800 border-gold/30">
                              <Calendar
                                mode="single"
                                selected={bookingData.returnDate}
                                onSelect={(date) => handleInputChange('returnDate', date)}
                                disabled={(date) => {
                                  return bookingData.date ? date < bookingData.date : date < new Date();
                                }}
                                locale={fr}
                                initialFocus
                              />
                            </PopoverContent>
                          </Popover>
                        </div>

                        {/* Return Time - Separate Hour and Minute Dropdowns */}
                        <div className="space-y-2">
                          <Label className="text-white text-base">Heure de retour *</Label>
                          <div className="flex gap-3 items-center">
                            {/* Hours Dropdown */}
                            <div className="flex-1">
                              <Select
                                value={bookingData.returnTime.split(':')[0] || ''}
                                onValueChange={(hour) => {
                                  const currentMinute = bookingData.returnTime.split(':')[1] || '00';
                                  handleInputChange('returnTime', `${hour}:${currentMinute}`);
                                }}
                              >
                                <SelectTrigger className="w-full bg-gray-800 border-gold/30 text-white hover:bg-gray-700">
                                  <div className="flex items-center justify-center">
                                    <Clock className="mr-2 h-4 w-4 text-gold" />
                                    {bookingData.returnTime.split(':')[0] || 'HH'}
                                  </div>
                                </SelectTrigger>
                                <SelectContent className="bg-gray-800 border-gold/30 max-h-[300px] overflow-y-auto">
                                  {(() => {
                                    const now = new Date();
                                    const selectedDate = bookingData.returnDate ? new Date(bookingData.returnDate) : null;
                                    const isToday = selectedDate && 
                                      selectedDate.getDate() === now.getDate() &&
                                      selectedDate.getMonth() === now.getMonth() &&
                                      selectedDate.getFullYear() === now.getFullYear();
                                    
                                    let startHour = 0;
                                    if (isToday) {
                                      const minimumTime = new Date(now.getTime() + 30 * 60000);
                                      startHour = minimumTime.getHours();
                                    }
                                    
                                    const hours = [];
                                    for (let h = startHour; h < 24; h++) {
                                      hours.push(h.toString().padStart(2, '0'));
                                    }
                                    
                                    return hours.map(hour => (
                                      <SelectItem key={hour} value={hour} className="text-white hover:bg-gray-700">
                                        {hour}
                                      </SelectItem>
                                    ));
                                  })()}
                                </SelectContent>
                              </Select>
                              <p className="text-xs text-gray-400 mt-1 text-center">Heures</p>
                            </div>

                            <span className="text-gold text-2xl font-bold">:</span>

                            {/* Minutes Dropdown */}
                            <div className="flex-1">
                              <Select
                                value={bookingData.returnTime.split(':')[1] || ''}
                                onValueChange={(minute) => {
                                  const currentHour = bookingData.returnTime.split(':')[0] || '00';
                                  handleInputChange('returnTime', `${currentHour}:${minute}`);
                                }}
                              >
                                <SelectTrigger className="w-full bg-gray-800 border-gold/30 text-white hover:bg-gray-700">
                                  <div className="flex items-center justify-center">
                                    {bookingData.returnTime.split(':')[1] || 'MM'}
                                  </div>
                                </SelectTrigger>
                                <SelectContent className="bg-gray-800 border-gold/30 max-h-[300px] overflow-y-auto">
                                  {(() => {
                                    const now = new Date();
                                    const selectedDate = bookingData.returnDate ? new Date(bookingData.returnDate) : null;
                                    const isToday = selectedDate && 
                                      selectedDate.getDate() === now.getDate() &&
                                      selectedDate.getMonth() === now.getMonth() &&
                                      selectedDate.getFullYear() === now.getFullYear();
                                    
                                    const selectedHour = bookingData.returnTime.split(':')[0];
                                    let startMinute = 0;
                                    
                                    if (isToday && selectedHour) {
                                      const minimumTime = new Date(now.getTime() + 30 * 60000);
                                      const minHour = minimumTime.getHours();
                                      const minMinute = minimumTime.getMinutes();
                                      
                                      // Si l'heure sélectionnée est l'heure minimum, commencer aux minutes minimum
                                      if (parseInt(selectedHour) === minHour) {
                                        startMinute = minMinute;
                                      }
                                    }
                                    
                                    const minutes = [];
                                    for (let m = startMinute; m < 60; m++) {
                                      minutes.push(m.toString().padStart(2, '0'));
                                    }
                                    
                                    return minutes.map(minute => (
                                      <SelectItem key={minute} value={minute} className="text-white hover:bg-gray-700">
                                        {minute}
                                      </SelectItem>
                                    ));
                                  })()}
                                </SelectContent>
                              </Select>
                              <p className="text-xs text-gray-400 mt-1 text-center">Minutes</p>
                            </div>
                          </div>
                          <p className="text-xs text-gray-500">Format 24h - Toutes les minutes disponibles</p>
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {/* Pickup Location */}
                <div className="space-y-2 md:col-span-2">
                  <Label htmlFor="pickup" className="text-white text-base">
                    {t('booking.pickup')} *
                  </Label>
                  <div className="relative">
                    <MapPin className="absolute left-3 top-3 h-4 w-4 text-gold z-10 pointer-events-none" />
                    <Input
                      ref={pickupInputRef}
                      id="pickup"
                      name="pickup"
                      placeholder="Ex: Rue de la Loi 16, 1000 Bruxelles"
                      value={bookingData.pickup}
                      onChange={(e) => {
                        handleInputChange('pickup', e.target.value);
                        if (errors.pickup) setErrors({...errors, pickup: null});
                      }}
                      className={`bg-gray-800 text-white pl-10 ${errors.pickup ? 'border-red-500 border-2' : 'border-gold/30'}`}
                      required
                    />
                  </div>
                  {errors.pickup && (
                    <p className="text-red-500 text-sm flex items-center gap-1">
                      <span>⚠️</span> {errors.pickup}
                    </p>
                  )}
                  <p className="text-xs text-gray-500">Commencez à taper, des suggestions apparaîtront</p>
                </div>

                {/* Dropoff Location - Only for transfer */}
                {bookingData.serviceType === 'transfer' && (
                  <div className="space-y-2 md:col-span-2">
                    <Label htmlFor="dropoff" className="text-white text-base">{t('booking.dropoff')} *</Label>
                    <div className="relative">
                      <MapPin className="absolute left-3 top-3 h-4 w-4 text-gold z-10 pointer-events-none" />
                      <Input
                        ref={dropoffInputRef}
                        id="dropoff"
                        name="dropoff"
                        placeholder="Ex: Aéroport de Bruxelles, 1930 Zaventem"
                        value={bookingData.dropoff}
                        onChange={(e) => {
                          handleInputChange('dropoff', e.target.value);
                          if (errors.dropoff) setErrors({...errors, dropoff: null});
                        }}
                        className={`bg-gray-800 text-white pl-10 ${errors.dropoff ? 'border-red-500 border-2' : 'border-gold/30'}`}
                        required
                      />
                    </div>
                    {errors.dropoff && (
                      <p className="text-red-500 text-sm flex items-center gap-1">
                        <span>⚠️</span> {errors.dropoff}
                      </p>
                    )}
                    <p className="text-xs text-gray-500">Commencez à taper, des suggestions apparaîtront</p>
                  </div>
                )}

                {/* Calculate Distance Button - Optionnel pour recalculer */}
                {bookingData.serviceType === 'transfer' && bookingData.pickup && bookingData.dropoff && (
                  <div className="md:col-span-2">
                    <Button
                      type="button"
                      onClick={handleCalculateDistance}
                      disabled={calculatingDistance}
                      variant="outline"
                      className="w-full border-gold/30 text-gold hover:bg-gold/10 font-semibold"
                    >
                      {calculatingDistance ? '⏳ Calcul en cours...' : '🔄 Recalculer la distance et le prix'}
                    </Button>
                  </div>
                )}

                {/* Affichage de la distance et durée calculées automatiquement */}
                {bookingData.serviceType === 'transfer' && bookingData.distance > 0 && (
                  <div className="md:col-span-2 bg-gold/10 border border-gold/30 rounded-lg p-4">
                    <div className="grid grid-cols-2 gap-4 text-white">
                      <div>
                        <p className="text-sm text-gray-400">Distance calculée</p>
                        <p className="text-2xl font-bold text-gold">{bookingData.distance} km</p>
                      </div>
                      <div>
                        <p className="text-sm text-gray-400">Durée estimée</p>
                        <p className="text-2xl font-bold text-gold">{bookingData.duration} min</p>
                      </div>
                    </div>
                    {bookingData.isOutsideBrussels && (
                      <div className="mt-3 bg-amber-900/30 border border-amber-600/50 rounded px-3 py-2">
                        <p className="text-sm text-amber-400">
                          ⚠️ {bookingData.distance >= 100 ? 'Hors zone (Flandre/Wallonie)' : 'Zone élargie (Brabant/périphérie)'}: +{bookingData.distance >= 100 ? '50' : pricingRules.outsideBrusselsSurcharge}€
                        </p>
                      </div>
                    )}
                  </div>
                )}

                {/* Hours - For corporate and hourly */}
                {(bookingData.serviceType === 'corporate' || bookingData.serviceType === 'hourly') && (
                  <div className="space-y-2 md:col-span-2">
                    <Label htmlFor="hours" className="text-white text-base">
                      {t('booking.hours')} * {bookingData.serviceType === 'hourly' && '(minimum 3h)'}
                    </Label>
                    <Input
                      id="hours"
                      type="number"
                      min={bookingData.serviceType === 'hourly' ? 3 : 1}
                      placeholder={t('booking.hoursPlaceholder')}
                      value={bookingData.hours || ''}
                      onChange={(e) => handleInputChange('hours', parseInt(e.target.value))}
                      className="bg-gray-800 border-gold/30 text-white"
                      required
                    />
                    <p className="text-xs text-gray-500">
                      {bookingData.serviceType === 'hourly' 
                        ? `${pricingRules.hourlyRate}€/heure` 
                        : `${pricingRules.corporatePerHour}€/heure`}
                    </p>
                  </div>
                )}
              </div>

              {/* Price Estimate */}
              {estimatedPrice > 0 && (
                <div className="bg-gradient-to-r from-gold/20 to-amber-600/20 border-2 border-gold/50 rounded-lg p-6 mt-6">
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center gap-3">
                      <Euro className="w-8 h-8 text-gold" />
                      <div>
                        <p className="text-gray-400 text-sm">{t('booking.estimatedPrice')}</p>
                        <p className="text-3xl font-bold text-white">{estimatedPrice.toFixed(2)}€</p>
                        
                        {/* Show discounts applied */}
                        <div className="mt-2 space-y-1">
                          {bookingData.isRoundTrip && bookingData.serviceType === 'transfer' && (
                            <p className="text-sm text-green-400 flex items-center gap-1">
                              <span className="text-xl">✓</span> Aller-retour (-10%)
                            </p>
                          )}
                          {loyaltyDiscount > 0 && (
                            <p className="text-sm text-amber-400 flex items-center gap-1">
                              <span className="text-xl">🎉</span> Fidélité (-{loyaltyDiscount}%)
                            </p>
                          )}
                          {(bookingData.isRoundTrip || loyaltyDiscount > 0) && (
                            <p className="text-xs text-gray-500 mt-1">
                              Prix après réductions appliquées
                            </p>
                          )}
                        </div>
                      </div>
                    </div>
                    <div className="text-right text-sm text-gray-400">
                      {bookingData.serviceType === 'transfer' && (
                        <>
                          <p>{t('services.pricing.basePrice')}: {pricingRules.basePrice}€</p>
                          <p>{bookingData.distance} km × {pricingRules.pricePerKm}€</p>
                          <p>{bookingData.duration} min × {pricingRules.pricePerMinute}€</p>
                        </>
                      )}
                      {bookingData.serviceType === 'corporate' && (
                        <>
                          <p>{t('services.pricing.basePrice')}: {pricingRules.basePrice}€</p>
                          <p>{bookingData.hours} heures × {pricingRules.corporatePerHour}€</p>
                        </>
                      )}
                      {bookingData.serviceType === 'hourly' && (
                        <p>{Math.max(bookingData.hours, 3)} heures × {pricingRules.hourlyRate}€</p>
                      )}
                    </div>
                  </div>
                  
                  {/* Loyalty Badge for logged-in customers */}
                  {isLoggedIn && customerData && (
                    <div className="mt-4 pt-4 border-t border-gold/30">
                      <p className="text-xs text-gray-400 mb-2">Programme Fidélité</p>
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <span className="text-2xl">🎁</span>
                          <div>
                            <p className="text-sm text-white font-medium">
                              {customerData.loyaltyPoints || 0} / 5 courses complétées
                            </p>
                            <p className="text-xs text-gray-500">
                              {loyaltyDiscount > 0 
                                ? "Réduction active !" 
                                : `Plus que ${5 - (customerData.loyaltyPoints || 0) % 5} pour -10%`
                              }
                            </p>
                          </div>
                        </div>
                        <div className="w-24 bg-gray-700 rounded-full h-2">
                          <div 
                            className="bg-gradient-to-r from-amber-500 to-yellow-500 h-2 rounded-full transition-all"
                            style={{ width: `${((customerData.loyaltyPoints || 0) % 5) * 20}%` }}
                          />
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Section Modification de Prix */}
              {estimatedPrice > 0 && (
                <div className="mt-4 p-4 bg-gray-800/50 rounded-lg border border-gold/20">
                  <div className="flex items-center justify-between mb-3">
                    <h4 className="text-white font-semibold text-sm flex items-center gap-2">
                      <Euro className="h-4 w-4 text-gold" />
                      Ajuster le prix (optionnel)
                    </h4>
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() => setShowPriceOverride(!showPriceOverride)}
                      className="text-xs text-gold hover:text-gold/80"
                    >
                      {showPriceOverride ? '▲ Masquer' : '▼ Afficher'}
                    </Button>
                  </div>
                  
                  {showPriceOverride && (
                    <div className="space-y-3">
                      {/* Boutons type de réduction */}
                      <div className="grid grid-cols-4 gap-2">
                        <Button
                          type="button"
                          variant={bookingData.discountType === 'percentage' ? 'default' : 'outline'}
                          size="sm"
                          onClick={() => setBookingData(prev => ({ ...prev, discountType: 'percentage', discountValue: 0 }))}
                          className={bookingData.discountType === 'percentage' ? 'bg-gold/90 hover:bg-gold text-black text-xs' : 'border-gold/30 text-gold hover:bg-gold/10 text-xs'}
                        >
                          %
                        </Button>
                        <Button
                          type="button"
                          variant={bookingData.discountType === 'fixed' ? 'default' : 'outline'}
                          size="sm"
                          onClick={() => setBookingData(prev => ({ ...prev, discountType: 'fixed', discountValue: 0 }))}
                          className={bookingData.discountType === 'fixed' ? 'bg-gold/90 hover:bg-gold text-black text-xs' : 'border-gold/30 text-gold hover:bg-gold/10 text-xs'}
                        >
                          €
                        </Button>
                        <Button
                          type="button"
                          variant={bookingData.discountType === 'manual' ? 'default' : 'outline'}
                          size="sm"
                          onClick={() => setBookingData(prev => ({ ...prev, discountType: 'manual', manualPrice: estimatedPrice }))}
                          className={bookingData.discountType === 'manual' ? 'bg-gold/90 hover:bg-gold text-black text-xs' : 'border-gold/30 text-gold hover:bg-gold/10 text-xs'}
                        >
                          Manuel
                        </Button>


                        <Button
                          type="button"
                          variant={bookingData.discountType === 'none' ? 'default' : 'outline'}
                          size="sm"
                          onClick={() => setBookingData(prev => ({ ...prev, discountType: 'none', discountValue: 0, manualPrice: 0 }))}
                          className={bookingData.discountType === 'none' ? 'bg-gold/90 hover:bg-gold text-black text-xs' : 'border-gold/30 text-gold hover:bg-gold/10 text-xs'}
                        >
                          Aucun
                        </Button>
                      </div>
                      
                      {/* Champs de saisie */}
                      {bookingData.discountType === 'percentage' && (
                        <div>
                          <Label className="text-gray-300 text-xs">Réduction % (max 50%)</Label>
                          <Input
                            type="number"
                            min="0"
                            max="50"
                            value={bookingData.discountValue}
                            onChange={(e) => {
                              const val = parseFloat(e.target.value) || 0;
                              if (val > 50) {
                                toast.error('Réduction max: 50%');
                                return;
                              }
                              setBookingData(prev => ({ ...prev, discountValue: val }));
                            }}
                            className="bg-gray-700 border-gold/30 text-white mt-1"
                            placeholder="10"
                          />
                          <p className="text-xs text-green-400 mt-1">
                            Final: {calculateFinalPrice().toFixed(2)}€ (min {(estimatedPrice * 0.5).toFixed(2)}€)
                          </p>
                        </div>
                      )}
                      
                      {bookingData.discountType === 'fixed' && (
                        <div>
                          <Label className="text-gray-300 text-xs">Réduction € (max 50% du prix)</Label>
                          <Input
                            type="number"
                            min="0"
                            max={estimatedPrice * 0.5}
                            value={bookingData.discountValue}
                            onChange={(e) => {
                              const val = parseFloat(e.target.value) || 0;
                              const maxDiscount = estimatedPrice * 0.5;
                              if (val > maxDiscount) {
                                toast.error(`Réduction max: ${maxDiscount.toFixed(2)}€ (50% du prix)`);
                                return;
                              }
                              setBookingData(prev => ({ ...prev, discountValue: val }));
                            }}
                            className="bg-gray-700 border-gold/30 text-white mt-1"
                            placeholder="5"
                          />
                          <p className="text-xs text-green-400 mt-1">
                            Final: {calculateFinalPrice().toFixed(2)}€ (min {(estimatedPrice * 0.5).toFixed(2)}€)
                          </p>
                        </div>
                      )}
                      
                      {bookingData.discountType === 'manual' && (
                        <div>
                          <Label className="text-gray-300 text-xs">Prix final € (min 50% du calculé)</Label>
                          <Input
                            type="number"
                            min={estimatedPrice * 0.5}
                            step="0.5"
                            value={bookingData.manualPrice}
                            onChange={(e) => {
                              const val = parseFloat(e.target.value) || 0;
                              const minPrice = estimatedPrice * 0.5;
                              if (val < minPrice && val > 0) {
                                toast.error(`Prix minimum: ${minPrice.toFixed(2)}€ (50% du prix calculé)`);
                                return;
                              }
                              setBookingData(prev => ({ ...prev, manualPrice: val }));
                            }}
                            className="bg-gray-700 border-gold/30 text-white mt-1"
                            placeholder={estimatedPrice.toFixed(2)}
                          />
                          <p className="text-xs text-gray-400 mt-1">
                            Calculé: {estimatedPrice.toFixed(2)}€ → Final: <span className="text-green-400">{calculateFinalPrice().toFixed(2)}€</span> (min {(estimatedPrice * 0.5).toFixed(2)}€)
                          </p>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}

              {/* Payment Method Choice - MOVED OUTSIDE OF PRICE ADJUSTMENT */}
              <div className="space-y-3 mt-6">
                <label className="block text-base font-semibold text-white">
                  Mode de paiement *
                </label>
                <div className="grid grid-cols-2 gap-4">
                  <button
                    type="button"
                    onClick={() => setBookingData({ ...bookingData, paymentMethod: 'online' })}
                    className={`p-4 rounded-lg border-2 transition-all ${
                      bookingData.paymentMethod === 'online'
                        ? 'border-gold bg-gold/10 text-gold'
                        : 'border-gray-700 bg-gray-800/50 text-gray-400 hover:border-gray-600'
                    }`}
                  >
                    <div className="flex flex-col items-center gap-2">
                      <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" />
                      </svg>
                      <div className="text-center">
                        <div className="font-semibold">Paiement en ligne</div>
                        <div className="text-xs opacity-75">Carte bancaire (Stripe)</div>
                      </div>
                      {bookingData.paymentMethod === 'online' && (
                        <div className="absolute top-2 right-2">
                          <svg className="w-5 h-5 text-gold" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                          </svg>
                        </div>
                      )}
                    </div>
                  </button>

                  <button
                    type="button"
                    onClick={() => setBookingData({ ...bookingData, paymentMethod: 'cash' })}
                    className={`p-4 rounded-lg border-2 transition-all relative ${
                      bookingData.paymentMethod === 'cash'
                        ? 'border-gold bg-gold/10 text-gold'
                        : 'border-gray-700 bg-gray-800/50 text-gray-400 hover:border-gray-600'
                    }`}
                  >
                    <div className="flex flex-col items-center gap-2">
                      <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 9V7a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2m2 4h10a2 2 0 002-2v-6a2 2 0 00-2-2H9a2 2 0 00-2 2v6a2 2 0 002 2zm7-5a2 2 0 11-4 0 2 2 0 014 0z" />
                      </svg>
                      <div className="text-center">
                        <div className="font-semibold">Paiement cash</div>
                        <div className="text-xs opacity-75">Espèces au chauffeur</div>
                      </div>
                      {bookingData.paymentMethod === 'cash' && (
                        <div className="absolute top-2 right-2">
                          <svg className="w-5 h-5 text-gold" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                          </svg>
                        </div>
                      )}
                    </div>
                  </button>
                </div>
                {bookingData.paymentMethod === 'online' && (
                  <div className="flex items-center gap-2 text-xs text-gray-400 bg-blue-500/10 border border-blue-500/30 rounded-lg p-3">
                    <svg className="w-4 h-4 text-blue-400" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                    </svg>
                    <span>Vous recevrez un lien de paiement sécurisé par email après confirmation</span>
                  </div>
                )}
                {bookingData.paymentMethod === 'cash' && (
                  <div className="flex items-center gap-2 text-xs text-gray-400 bg-amber-500/10 border border-amber-500/30 rounded-lg p-3">
                    <svg className="w-4 h-4 text-amber-400" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                    </svg>
                    <span>Vous paierez directement le chauffeur en espèces</span>
                  </div>
                )}
              </div>

              {/* Submit Button */}
              <Button 
                type="button"
                onClick={handleSubmit}
                size="lg"
                disabled={loading}
                className="w-full bg-gradient-to-r from-gold to-amber-600 text-black hover:from-amber-600 hover:to-gold text-lg py-6 shadow-lg shadow-gold/20"
              >
                {loading ? t('booking.processing') : t('booking.confirmBooking')}
              </Button>
            </form>
          </CardContent>
        </Card>
      </div>
    </section>
  );
};

export default BookingSection;
